import { useCallback, useState } from "react"

import { graphql } from "@/company_admin/gql/__generated__"
import { useCompanyAdminClient } from "@/provider/apollo/Apollo"

// --- GraphQL ---

const DownloadSettlementExcelAndInvoicePDF = graphql(/* GraphQL */ `
	query DownloadSettlementExcelAndInvoicePDF(
		$param: DownloadSettlementExcelAndInvoicePDFParam!
	) {
		downloadSettlementExcelAndInvoicePDF(param: $param) {
			excel {
				downloadUrl
				filename
			}
			invoicePdf {
				downloadUrl
				filename
			}
		}
	}
`)

// --- Helper ---

// Trigger a file download using a hidden iframe.
// Each download runs in its own iframe (separate browsing context) so multiple
// downloads never cancel each other — even on slow connections.
// The presigned URL includes Content-Disposition: attachment, so the browser
// downloads the file instead of displaying it in the iframe.
const triggerDownload = (url: string, _filename: string) => {
	const iframe = document.createElement("iframe")
	iframe.style.display = "none"
	iframe.src = url
	document.body.appendChild(iframe)
	// Clean up iframe after 5 minutes
	setTimeout(() => iframe.remove(), 300_000)
}

// --- Hook ---

type UseExcelDownloadReturn = {
	downloadExcel: (settlementId: string) => Promise<void>
	downloadingSettlementId: string | null
}

const useExcelDownload = (): UseExcelDownloadReturn => {
	const client = useCompanyAdminClient()
	const [downloadingSettlementId, setDownloadingSettlementId] = useState<
		string | null
	>(null)

	const downloadExcel = useCallback(
		async (settlementId: string) => {
			setDownloadingSettlementId(settlementId)
			return await client
				.query({
					query: DownloadSettlementExcelAndInvoicePDF,
					variables: { param: { settlementId } },
					fetchPolicy: "network-only",
				})
				.then(({ data }) => {
					const result = data.downloadSettlementExcelAndInvoicePDF

					triggerDownload(result.excel.downloadUrl, result.excel.filename)

					if (result.invoicePdf) {
						triggerDownload(
							result.invoicePdf.downloadUrl,
							result.invoicePdf.filename
						)
					}
				})
				.catch((e) => {
					// Error display is handled by Apollo.tsx global error handling.
					// Just log the error for debugging.
					console.error("Excel download failed:", e)
				})
				.finally(() => {
					setDownloadingSettlementId(null)
				})
		},
		[client]
	)

	return { downloadExcel, downloadingSettlementId }
}

export default useExcelDownload
